import {
  ColDef,
  GridOptions,
  themeAlpine,
  ValueFormatterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IgGrayPrimitives } from "@intelligems/ig-design-tokens/constants";
import { noDecimalNumberFormatter } from "@intelligems/ig-graphs/data-formatters";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { OrderExampleDrawer } from "@src/features/analytics/baseDashboard/OrderExampleDrawer";
import { removePrefix } from "@src/features/analytics/baseDashboard/utils";
import { dataReconciliationReasonDescriptionMapping } from "@src/features/analytics/utils";
import { getDrawer, openDrawer } from "@src/store/slices/drawers";
import { theme } from "@src/theme";
import { UseQueryResult } from "@tanstack/react-query";

const myTheme = themeAlpine.withParams({
  fontFamily: theme.typography.fontFamily,
  rowBorder: `1px solid ${IgGrayPrimitives[600]}`,
  headerRowBorder: `1px solid ${IgGrayPrimitives[600]}`,
  headerFontWeight: 600,
  headerTextColor: IgGrayPrimitives["A300"],
  headerFontSize: "14px",
  wrapperBorder: "0px solid",
  wrapperBorderRadius: "15px",
});

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .ag-header-cell:not(:last-child)`]: {
    borderRight: `1px solid ${theme.igPrimitives.gray[600]}`,
    justifyContent: "center !important",
  },
  [`& .ag-header-group-cell:not(:last-child)`]: {
    borderRight: `1px solid ${theme.igPrimitives.gray[600]}`,
    justifyContent: "center !important", // centering grouped headers
    textAlign: "center",
    display: "flex !important",
  },
  [`& .ag-header-cell-text`]: {
    color: `${theme.palette.text.secondary} !important`,
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "18px",
    letterSpacing: ".25px",
  },
  [`& .ag-header-custom`]: {
    "& .ag-header-cell-label": {
      justifyContent: "center !important",
    },
  },
  [`& .ag-header-cell-comp-wrapper`]: {
    justifyContent: "center !important", // centering grouped headers
  },
  [`& .ag-cell-norm`]: {
    [`&.ag-cell`]: {
      borderRight: `1px solid ${theme.igPrimitives.gray[600]}`,
    },
  },
  [`& .ag-cell-numeric`]: {
    [`&.ag-cell`]: {
      textAlign: "center",
      fontSize: "16px",
      lineHeight: "24px",
      letterSpacing: ".15px",
      [`&.ag-cell:not(:last-child)`]: {
        borderRight: `1px solid ${theme.igPrimitives.gray[600]}`,
      },
    },
  },
  [`& .ag-cell-wrapper.ag-row-group`]: {
    alignItems: "center !important",
  },
  [`& .ag-center-cols-viewport`]: {
    minHeight: "120px !important",
  },
}));

interface DataReconciliationTableProps {
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
}

export const OrdersReconciliationTable = ({
  data,
}: DataReconciliationTableProps) => {
  const dispatch = useDispatch();
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const orderExamplesDrawer = useSelector(getDrawer("orderExamples"));

  const gridRef = useRef<AgGridReact>(null);
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      minWidth: 50,
      resizable: false,
      cellStyle: { fontSize: "14px" },
      menuTabs: [],
      suppressMovable: true,
      suppressHeaderMenuButton: true,
    };
  }, []);

  const columnDefs: ColDef[] = useMemo(() => {
    return [
      {
        headerName: "Reason",
        headerClass: "header-custom",
        field: "exclusion_reason",
        cellStyle: {
          justifyContent: "flex-start",
        },
        flex: 0.6,
        valueFormatter: (params: ValueFormatterParams) => {
          return removePrefix(params?.value || "");
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (params) => {
          return (
            <IgTypography variant={"body2"}>
              {params.valueFormatted}
            </IgTypography>
          );
        },
      },
      {
        headerName: "# of Orders",
        headerClass: "header-custom",
        field: "n_orders",
        flex: 0.2,
        cellStyle: {
          justifyContent: "flex-start",
        },

        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (params) => {
          return (
            <IgTypography variant={"body2"}>
              {params.valueFormatted}
            </IgTypography>
          );
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return noDecimalNumberFormatter(
            params.value,
            false,
            storeCurrencyConfig?.value as { code: string; locale: string }
          );
        },
      },
      {
        headerName: "Description",
        headerClass: "header-custom",
        field: "mappedDescription",
        flex: 1,
        cellStyle: {
          justifyContent: "flex-start",
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return dataReconciliationReasonDescriptionMapping[
            removePrefix(params.data["exclusion_reason"] || "")
          ];
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (params) => {
          return (
            <IgTypography
              variant={"body2"}
              sx={{
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
            >
              <IgTooltip title={params.valueFormatted}>
                {params.valueFormatted}
              </IgTooltip>
            </IgTypography>
          );
        },
      },
      {
        headerName: "Example Orders",
        headerClass: "header-custom",
        flex: 0.3,
        field: "example_orders",
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (params) => {
          return (
            <IgTypography
              onClick={() => {
                dispatch(
                  openDrawer({
                    type: "orderExamples",
                    data: params.data,
                  })
                );
              }}
              variant={"body2"}
              sx={{
                color: theme.palette.text.interactive.neutral.default,
                "&:hover": {
                  cursor: "pointer",
                  color: theme.palette.text.interactive.neutral.hover,
                },
              }}
            >
              See Orders
            </IgTypography>
          );
        },
        cellStyle: {
          justifyContent: "flex-start",
        },
      },
    ];
  }, [data]);

  const loadingCellRendererParams = useMemo(() => {
    return {
      loadingMessage: "One moment please...",
    };
  }, []);

  const gridOptions = useMemo<GridOptions>(() => {
    return {
      rowData: data[0].data?.metrics || [],
    };
  }, []);

  return (
    <Stack spacing={2} direction={"column"} sx={{ width: "100%" }}>
      <StyledGrid size={"grow"} sx={{ marginTop: "0px", width: "100%" }}>
        <AgGridReact
          theme={myTheme}
          ref={gridRef}
          loading={data[0].isLoading || data[0].isPending}
          defaultColDef={defaultColDef}
          rowData={data[0].data?.metrics || []}
          columnDefs={columnDefs}
          gridOptions={gridOptions}
          suppressDragLeaveHidesColumns={true}
          suppressExcelExport={true}
          suppressCsvExport={true}
          headerHeight={28}
          rowHeight={40}
          domLayout={"autoHeight"}
          overlayLoadingTemplate={
            "<div>Loading Exclusion Order Data ... </div>"
          }
          loadingCellRendererParams={loadingCellRendererParams}
        />
      </StyledGrid>
      {orderExamplesDrawer?.data && <OrderExampleDrawer />}
    </Stack>
  );
};
